import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, query, where, updateDoc, doc } from 'firebase/firestore';
import { db } from '@/firebase';
import { useMunicipal } from '@/contexts/MunicipalContext';
import { Municipality, MunicipalEvent, MunicipalAnnouncement } from '@/types';
import { Building2, Trash2, CalendarDays, Megaphone, Search, Plus, X, MapPin, ExternalLink, AlertCircle, CheckCircle } from 'lucide-react';

interface Props {
  householdId: string;
  subscribedMunicipalityIds: string[];
  onSubscribe: (ids: string[]) => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  garbage: 'bg-gray-500',
  recycling: 'bg-green-500',
  organics: 'bg-lime-500',
  bulky: 'bg-orange-500',
  other: 'bg-blue-500',
};

const EVENT_CATEGORY_LABELS: Record<string, string> = {
  concert: 'Concert', market: 'Market', public_meeting: 'Meeting',
  sport: 'Sport', festival: 'Festival', other: 'Event',
};

export const MunicipalServices: React.FC<Props> = ({ householdId, subscribedMunicipalityIds, onSubscribe }) => {
  const { t } = useTranslation();
  const { municipalities, allSchedules, allEvents, allAnnouncements, getUpcomingPickups } = useMunicipal();
  const [tab, setTab] = useState<'schedules' | 'events' | 'announcements'>('schedules');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Municipality[]>([]);
  const [searching, setSearching] = useState(false);
  const [expandedAnnouncement, setExpandedAnnouncement] = useState<string | null>(null);
  const [eventCategory, setEventCategory] = useState<string>('all');

  const isSubscribed = subscribedMunicipalityIds.length > 0;

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setSearching(true);
    try {
      const snap = await getDocs(collection(db, 'municipalities'));
      const term = searchTerm.toLowerCase();
      const results = snap.docs
        .map(d => ({ id: d.id, ...d.data() } as Municipality))
        .filter(m =>
          m.status === 'active' &&
          (m.name.toLowerCase().includes(term) || m.region.toLowerCase().includes(term))
        );
      setSearchResults(results);
    } finally {
      setSearching(false);
    }
  };

  const handleSubscribe = async (munId: string) => {
    if (subscribedMunicipalityIds.includes(munId)) return;
    const newIds = [...subscribedMunicipalityIds, munId];
    await updateDoc(doc(db, 'households', householdId), { subscribedMunicipalityIds: newIds });
    onSubscribe(newIds);
  };

  const handleUnsubscribe = async (munId: string) => {
    const newIds = subscribedMunicipalityIds.filter(id => id !== munId);
    await updateDoc(doc(db, 'households', householdId), { subscribedMunicipalityIds: newIds });
    onSubscribe(newIds);
  };

  const now = new Date();
  const nowStr = now.toISOString();

  const upcomingPickups = getUpcomingPickups(28);

  const filteredEvents = allEvents
    .filter(e => e.status === 'published')
    .filter(e => eventCategory === 'all' || e.category === eventCategory)
    .sort((a, b) => a.startDateTime.localeCompare(b.startDateTime));

  const sortedAnnouncements = [...allAnnouncements]
    .filter(a => a.status === 'published' && (!a.expiresAt || a.expiresAt > nowStr))
    .sort((a, b) => {
      if (a.isUrgent && !b.isUrgent) return -1;
      if (!a.isUrgent && b.isUrgent) return 1;
      return b.createdAt.localeCompare(a.createdAt);
    });

  // Setup state
  if (!isSubscribed) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4">
        <div className="flex items-center gap-3 mb-6">
          <Building2 className="w-6 h-6 text-brand" />
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          {t('municipal.subscribe_desc', 'Subscribe to your local municipality to receive pickup schedules, community events, and announcements.')}
        </p>

        <div className="flex gap-3 mb-4">
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder={t('municipal.search_placeholder', 'Search by city or region...')}
            className="flex-1 px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-2xl bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          />
          <button
            onClick={handleSearch}
            disabled={searching}
            className="px-4 py-2.5 bg-brand hover:bg-brand-hover text-white rounded-2xl text-sm font-bold transition-all flex items-center gap-2"
          >
            <Search className="w-4 h-4" /> {t('municipal.search_btn', 'Search')}
          </button>
        </div>

        {searchResults.length > 0 && (
          <div className="space-y-2">
            {searchResults.map(m => (
              <div key={m.id} className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
                <div>
                  <p className="font-bold text-slate-900 dark:text-white">{m.name}</p>
                  <p className="text-xs text-slate-500">{m.region} · {m.subscriberCount} {t('municipal.subscribers', 'subscribers')}</p>
                </div>
                <button
                  onClick={() => handleSubscribe(m.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-brand hover:bg-brand-hover text-white rounded-xl text-xs font-bold transition-all"
                >
                  <Plus className="w-3.5 h-3.5" /> {t('municipal.subscribe_btn', 'Subscribe')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-6 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-brand" />
        </div>
        <div className="flex items-center gap-2">
          {municipalities.map(m => (
            <div key={m.id} className="flex items-center gap-1.5 px-3 py-1 bg-indigo-50 dark:bg-indigo-900/20 rounded-full">
              <span className="text-xs font-bold text-indigo-700 dark:text-indigo-300">{m.name}</span>
              <button onClick={() => handleUnsubscribe(m.id)} className="text-indigo-400 hover:text-brand">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-2xl mb-6">
        {([
          { key: 'schedules', label: t('municipal.tab_schedules', 'Schedules'), icon: CalendarDays },
          { key: 'events', label: t('municipal.tab_events', 'Events'), icon: Building2 },
          { key: 'announcements', label: t('municipal.tab_announcements', 'Announcements'), icon: Megaphone },
        ] as const).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all ${
              tab === key
                ? 'bg-white dark:bg-slate-700 text-brand dark:text-indigo-400 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Schedules Tab */}
      {tab === 'schedules' && (
        <div className="space-y-2">
          {upcomingPickups.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No upcoming pickups in the next 28 days.</div>
          ) : (
            upcomingPickups.map(({ schedule, date, exception }, idx) => (
              <div key={`${schedule.id}-${date}-${idx}`} className="flex items-center gap-4 p-4 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${CATEGORY_COLORS[schedule.category] ?? 'bg-blue-500'}`} />
                <div className="flex-1">
                  <p className="font-bold text-slate-900 dark:text-white text-sm">{schedule.title}</p>
                  <p className="text-xs text-slate-500">{schedule.category}</p>
                </div>
                <div className="text-right">
                  {exception ? (
                    <span className="text-xs font-bold text-amber-600 dark:text-amber-400">
                      {exception.newDate ? `Rescheduled → ${exception.newDate}` : 'Cancelled'}
                    </span>
                  ) : (
                    <span className="text-xs font-mono text-slate-600 dark:text-slate-300">{date}</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Events Tab */}
      {tab === 'events' && (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap mb-3">
            {['all', ...Object.keys(EVENT_CATEGORY_LABELS)].map(cat => (
              <button
                key={cat}
                onClick={() => setEventCategory(cat)}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                  eventCategory === cat
                    ? 'bg-brand text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                }`}
              >
                {cat === 'all' ? 'All' : EVENT_CATEGORY_LABELS[cat]}
              </button>
            ))}
          </div>
          {filteredEvents.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No upcoming events.</div>
          ) : (
            filteredEvents.map(event => (
              <div key={event.id} className="p-4 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-slate-900 dark:text-white text-sm">{event.title}</p>
                    <span className="text-2xs font-bold uppercase tracking-widest text-brand dark:text-indigo-400">
                      {EVENT_CATEGORY_LABELS[event.category] ?? event.category}
                    </span>
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    <p>{new Date(event.startDateTime).toLocaleDateString()}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300">{event.description}</p>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</span>
                  {event.registrationUrl && (
                    <a href={event.registrationUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-brand hover:underline">
                      <ExternalLink className="w-3 h-3" /> Register
                    </a>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Announcements Tab */}
      {tab === 'announcements' && (
        <div className="space-y-2">
          {sortedAnnouncements.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No active announcements.</div>
          ) : (
            sortedAnnouncements.map(ann => (
              <div
                key={ann.id}
                onClick={() => setExpandedAnnouncement(expandedAnnouncement === ann.id ? null : ann.id)}
                className="p-4 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 cursor-pointer hover:border-indigo-300 transition-all"
              >
                <div className="flex items-start gap-3">
                  {ann.isUrgent
                    ? <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    : <CheckCircle className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
                  }
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-bold text-slate-900 dark:text-white text-sm">{ann.title}</p>
                      {ann.isUrgent && (
                        <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-2xs font-black uppercase rounded-md">Urgent</span>
                      )}
                    </div>
                    {expandedAnnouncement === ann.id && (
                      <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">{ann.body}</p>
                    )}
                    <p className="text-2xs text-slate-400 mt-1">{new Date(ann.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
